// @flow
import * as React from "react";
import { ErrorMessage, FormikProvider } from "formik";
import { PAGE_LOGIN } from "../../locales/pages/namespaces";
import { useTranslation } from "react-i18next";
import {
  Button,
  FormControl,
  FormHelperText,
  Grid,
  Stack,
  Link,
  TextField,
  Typography,
  IconButton,
  useTheme,
  InputAdornment,
  useMediaQuery,
  OutlinedInput,
} from "@mui/material";
import { Google } from "@mui/icons-material";
import useSignUpForm from "./useSignUpForm";
import { Link as RouterLink, useHistory } from "react-router-dom";
import useUserData from "../../hooks/user/useUserData";
import AuthCardWrapper from "../../theme/mantis/sections/auth/authCardWrapper";
import { getRouteUrl } from "../../helpers/getRouteUrl";
import { ROUTE_PAGE_HOME, ROUTE_PAGE_LOGIN } from "../../routers/routes";
import { Capacitor } from "@capacitor/core";
import { GoogleLogin } from "@react-oauth/google";
import { GoogleAuth } from "@codetrix-studio/capacitor-google-auth";
import useUserAction from "../../hooks/user/useUserAction";
import EmailIcon from "../../assets/images/icons/login/emailIcon";
import PasswordIcon from "../../assets/images/icons/login/passwordIcon";
import VisibleEyeOff from "../../assets/images/icons/login/visibleEyeOff";
import VisibleEyeOn from "../../assets/images/icons/login/visibleEyeOn";
import ArrowDownCircle from "../../assets/images/icons/buttonsIcons/arrowDownCircle";
import { ThemeMode } from "../../theme/mantis/config";
import { Fragment, useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { handleAxiosErrors } from "../../helpers/errorHandling";
import MuiPhoneInput from "../../components/MuiPhoneInput/MuiPhoneInput";
import useCheckEmail from "../../modules/parameters/hooks/useCheckEmail";
import useCheckPhone from "../../modules/parameters/hooks/useCheckPhone";

type Props = {
  id: string,
  isUser: boolean,
};

const SignUp = (props: Props) => {
  const { t } = useTranslation(PAGE_LOGIN);
  useEffect(() => {
    document.title = t("IMS Financial Consulting | Sign up");
  }, [t]);
  const history = useHistory();
  const { id, isUser } = props;
  const { loading, language } = useUserData();
  const theme = useTheme();
  const { googleLogin } = useUserAction();
  const matchDownMD = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const { checkEmail } = useCheckEmail();
  const { checkPhone } = useCheckPhone();
  const [showPhoneInput, setShowPhoneInput] = useState(false);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);

  const {
    formik,
    showPassword,
    handleClickShowPassword,
    handleMouseDownPassword,
    setSelectedCountryCode,
  } = useSignUpForm({
    customer: { id, isUser },
    onCheckPhone: checkPhone,
    showPhoneInput,
  });

  async function googleNativeLogin() {
    const result = await GoogleAuth.signIn();
    console.info("result", result);
    if (result)
      googleLogin({
        credential: result?.authentication?.idToken,
        clientId: "",
        select_by: "",
      });
    history.push(getRouteUrl(ROUTE_PAGE_HOME));
  }

  const handleEmailBlur = async (e) => {
    formik.handleBlur(e);

    const email = e.target.value;

    if (!email || !email.includes("@")) {
      return;
    }

    setIsCheckingEmail(true);

    try {
      const response = await checkEmail(email);

      // Show phone input if validation=true (existing client found)
      if (response?.validation) {
        setShowPhoneInput(true);
      } else {
        setShowPhoneInput(false);
      }
    } catch (error) {
      setShowPhoneInput(false);
    } finally {
      setIsCheckingEmail(false);
    }
  };

  return (
    <AuthCardWrapper>
      <Grid container spacing={3} style={{ fontFamily: "IMS" }}>
        <Grid item xs={12} className="force-ims">
          <Fragment>
            <Typography
              className="color-default title-sm"
              sx={{ margin: "26px auto 0 auto" }}
            >
              {t("Get Started with IMS")}
            </Typography>

            <Typography className="p-sm color-secondary">
              {t("Create a free account")}
            </Typography>

            <FormikProvider value={formik}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Stack spacing={1}>
                    <FormControl
                      variant="standard"
                      fullWidth
                      error={
                        formik.touched["email"] &&
                        Boolean(formik.errors["email"])
                      }
                    >
                      <OutlinedInput
                        placeholder={t("Email")}
                        type={"email"}
                        value={formik.values.email}
                        name={`email`}
                        onChange={formik.handleChange}
                        onBlur={(e) => handleEmailBlur(e)}
                        fullWidth
                        autoComplete="on"
                        color={"grey"}
                        size={"small"}
                        required
                        error={
                          formik.touched.email && Boolean(formik.errors.email)
                        }
                        id="email"
                        className="border-neutral-dark-radius"
                        sx={{ minHeight: "unset", margin: "30px auto 0 auto" }}
                        startAdornment={
                          <InputAdornment
                            position="start"
                            sx={{ bgColor: "#FFFFFF" }}
                          >
                            <EmailIcon />
                          </InputAdornment>
                        }
                        inputProps={{
                          style: {
                            fontSize: "14px",
                            padding: "10px",
                            minHeight: "unset",
                          },
                        }}
                      />
                      {formik.touched.email && Boolean(formik.errors.email) && (
                        <FormHelperText
                          error
                          id="standard-weight-helper-text-email-login"
                        >
                          {formik.errors.email}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Stack>
                </Grid>
                {showPhoneInput && (
                  <Grid item xs={12}>
                    <Stack spacing={1}>
                      <MuiPhoneInput
                        value={formik.values.phone || ""}
                        onChange={(phone) =>
                          formik.setFieldValue("phone", phone)
                        }
                        onCountryChange={setSelectedCountryCode}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched["phone"] &&
                          Boolean(formik.errors["phone"])
                        }
                        helperText={
                          formik.touched["phone"] && formik.errors["phone"]
                            ? formik.errors["phone"]
                            : ""
                        }
                        placeholder={t("Phone number")}
                        sx={{ margin: "8px auto 0 auto" }}
                      />
                    </Stack>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Stack spacing={1}>
                    <FormControl
                      fullWidth
                      error={
                        formik.touched["password"] &&
                        Boolean(formik.errors["password"])
                      }
                      sx={{ mb: 2 }}
                    >
                      <OutlinedInput
                        placeholder={t("Create a Password")}
                        type={showPassword ? "text" : "password"}
                        value={formik.values.password}
                        name={`password`}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        fullWidth
                        autoComplete="new-password"
                        color={"grey"}
                        size={"small"}
                        required
                        error={
                          formik.touched.password &&
                          Boolean(formik.errors.password)
                        }
                        className="border-neutral-dark-radius"
                        sx={{
                          minHeight: "unset",
                          margin: "8px auto 0 auto",
                          paddingLeft: "4px",
                        }}
                        startAdornment={
                          <InputAdornment position="start">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleClickShowPassword}
                              onMouseDown={handleMouseDownPassword}
                              edge="end"
                              color={theme.palette.primary["100"]}
                            >
                              {showPassword ? (
                                <VisibleEyeOff />
                              ) : (
                                <VisibleEyeOn />
                              )}
                            </IconButton>
                          </InputAdornment>
                        }
                        inputProps={{
                          style: {
                            fontSize: "14px",
                            padding: "10px",
                            minHeight: "unset",
                          },
                        }}
                      />
                      {formik.touched.password &&
                        Boolean(formik.errors.password) && (
                          <FormHelperText
                            error
                            id="standard-weight-helper-text-email-login"
                          >
                            {formik.errors.password}
                          </FormHelperText>
                        )}
                    </FormControl>
                  </Stack>
                </Grid>
                <Grid item xs={12}>
                  <Stack spacing={1}>
                    <FormControl
                      fullWidth
                      error={
                        formik.touched["confirmPassword"] &&
                        Boolean(formik.errors["confirmPassword"])
                      }
                      sx={{ mb: 2 }}
                    >
                      <OutlinedInput
                        placeholder={t("Confirm your Password")}
                        type={showPassword ? "text" : "password"}
                        value={formik.values.confirmPassword}
                        name={`confirmPassword`}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        fullWidth
                        autoComplete="on"
                        color={"grey"}
                        size={"small"}
                        required
                        error={
                          formik.touched.confirmPassword &&
                          Boolean(formik.errors.confirmPassword)
                        }
                        className="border-neutral-dark-radius"
                        sx={{
                          minHeight: "unset",
                          margin: "8px auto 0 auto",
                          paddingLeft: "4px",
                        }}
                        startAdornment={
                          <InputAdornment position="start">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleClickShowPassword}
                              onMouseDown={handleMouseDownPassword}
                              edge="end"
                              color={theme.palette.primary["100"]}
                            >
                              {showPassword ? (
                                <VisibleEyeOff />
                              ) : (
                                <VisibleEyeOn />
                              )}
                            </IconButton>
                          </InputAdornment>
                        }
                        inputProps={{
                          style: {
                            fontSize: "14px",
                            padding: "10px",
                            minHeight: "unset",
                          },
                        }}
                      />
                      {formik.touched.confirmPassword &&
                        Boolean(formik.errors.confirmPassword) && (
                          <FormHelperText
                            error
                            id="standard-weight-helper-text-email-login"
                          >
                            {formik.errors.confirmPassword}
                          </FormHelperText>
                        )}
                    </FormControl>
                  </Stack>
                </Grid>
                {/*TODO::Hide google login*/}
                {/*{!(Capacitor.getPlatform() === 'ios') && (
									<Grid item xs={12}>
										{Capacitor.getPlatform() === 'android' ? (
											<Button onClick={googleNativeLogin}>
												<Google/>{t("Sign in with Google")}
											</Button>
										) : (
											<GoogleLogin
												size="large"
												//shape="circle"
												width={matchDownMD ? '280px' : '345px'}
												onSuccess={credentialResponse => {
													googleLogin(credentialResponse)
													history.push(getRouteUrl(ROUTE_PAGE_HOME))
												}}
												onError={(error) => {
													enqueueSnackbar(handleAxiosErrors(error, t('Something went wrong!')), {
															variant: 'error'
														}
													)
												}}
												useOneTap
												theme={'filled_white'}
												locale={language}
											/>
										)}
									</Grid>
								)}*/}
                <Grid
                  item
                  xs={12}
                  textAlign={"center"}
                  sx={{ margin: "2px auto 2px" }}
                >
                  <Typography className="p-sm color-secondary" component="span">
                    {t("Already have an account?")}{" "}
                    <Link
                      component={RouterLink}
                      variant="h5"
                      to={getRouteUrl(ROUTE_PAGE_LOGIN)}
                      underline="hover"
                      color="textPrimary"
                      className="p-sm color-default"
                    >
                      {t("Sign In")}
                    </Link>
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={12}
                  textAlign={"center"}
                  sx={{ marginBottom: "28px" }}
                >
                  <Button
                    disableElevation
                    disabled={loading || isCheckingEmail}
                    fullWidth
                    sx={{ p: "14px 0", marginBottom: "4px" }}
                    size={"small"}
                    variant={"contained"}
                    color={"info"}
                    type="submit"
                    onClick={formik.submitForm}
                    className="button-primary"
                  >
                    {t("Sign up")}
                  </Button>
                </Grid>
              </Grid>
            </FormikProvider>
          </Fragment>
        </Grid>
      </Grid>
    </AuthCardWrapper>
  );
};
export default SignUp;
